import type {
  CanonicalArtifactRecord,
  CanonicalModelInstanceClassification,
  CanonicalModelInstanceRef,
} from "../../api/canonicalLineage";
import type { DagViewerGraph, DagViewerNode } from "./types";
import "./model-classification.css";

export const MODEL_CLASSIFICATION_LABELS: Record<CanonicalModelInstanceClassification, string> = {
  generic: "Generic model",
  instance: "Model instance",
  unclassified: "Unclassified",
};

/**
 * Read model semantics only from the strictly parsed canonical record. User
 * metadata, names and Artifact kinds are deliberately not classification
 * evidence.
 */
export function exactModelInstanceForNode(node: DagViewerNode): CanonicalModelInstanceRef | null {
  const value = node.metadata.canonical_record;
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Partial<CanonicalArtifactRecord>;
  if (
    record.entity_kind !== "artifact"
    || record.id !== node.metadata.canonical_id
    || record.model_instance === null
    || typeof record.model_instance !== "object"
  ) return null;
  const classification = record.model_instance.classification;
  const genericArtifactId = record.model_instance.generic_artifact_id;
  if (
    classification !== "generic"
    && classification !== "instance"
    && classification !== "unclassified"
  ) return null;
  if ((classification === "instance") !== (typeof genericArtifactId === "string")) return null;
  return record.model_instance as CanonicalModelInstanceRef;
}

export function visibleModelClassification(node: DagViewerNode): {
  classification: CanonicalModelInstanceClassification;
  compatibilityFallback: boolean;
} | null {
  const exact = exactModelInstanceForNode(node);
  if (exact) return { classification: exact.classification, compatibilityFallback: false };
  // Historical/local Artifact presentations without the canonical field are
  // explicitly unknown. “Unclassified” is a disclosure-local presentation,
  // never a guess from kind, name or arbitrary metadata.
  if (node.semanticRole === "artifact" && node.metadata.boundary !== true) {
    return { classification: "unclassified", compatibilityFallback: true };
  }
  return null;
}

/** Resolve only an exact, fully materialized generic Artifact target. */
export function exactVisibleGenericModel(
  graph: DagViewerGraph,
  node: DagViewerNode,
): DagViewerNode | null {
  const relation = exactModelInstanceForNode(node);
  if (relation?.classification !== "instance" || relation.generic_artifact_id === null) return null;
  const target = graph.nodes[graph.nodeIndexById.get(`artifact:${relation.generic_artifact_id}`) ?? -1] ?? null;
  return target
    && target.metadata.interactive !== false
    && exactModelInstanceForNode(target)?.classification === "generic"
    ? target
    : null;
}

/** Resolve the reverse relation from canonical instance records already in this bounded graph. */
export function exactVisibleModelInstances(
  graph: DagViewerGraph,
  node: DagViewerNode,
): DagViewerNode[] {
  const relation = exactModelInstanceForNode(node);
  if (relation?.classification !== "generic") return [];
  const genericArtifactId = node.metadata.canonical_id;
  if (typeof genericArtifactId !== "string") return [];
  return graph.nodes.filter((candidate) => {
    if (candidate.metadata.interactive === false) return false;
    const candidateRelation = exactModelInstanceForNode(candidate);
    return candidateRelation?.classification === "instance"
      && candidateRelation.generic_artifact_id === genericArtifactId;
  });
}

export function ModelClassificationBadge({
  classification,
  compatibilityFallback = false,
}: {
  classification: CanonicalModelInstanceClassification;
  compatibilityFallback?: boolean;
}) {
  const label = MODEL_CLASSIFICATION_LABELS[classification];
  return (
    <span
      className={`dag-model-classification-badge is-${classification}`}
      data-model-classification={classification}
      data-classification-source={compatibilityFallback ? "compatibility-unclassified" : "canonical"}
      aria-label={`${label}${compatibilityFallback ? "; canonical classification not recorded" : ""}`}
      title={compatibilityFallback
        ? "Canonical model classification was not recorded; no classification was inferred."
        : label}
    >
      {label}
    </span>
  );
}

export function NodeModelClassificationBadge({ node }: { node: DagViewerNode }) {
  const presentation = visibleModelClassification(node);
  return presentation ? <ModelClassificationBadge {...presentation} /> : null;
}
